import { STATUS } from "@/constants/statuses";
import { applyTransaction } from "@datorama/akita";
import { Observable, catchError, map, tap } from "rxjs";
import { create } from "superstruct";
import { inject, singleton } from "tsyringe";

import type { LoggerInterface } from "@interfaces/LoggerInterface";

import NudgesCustomerActiveRewardsApiService from "../api_services/NudgesCustomerActiveRewardsApiService/NudgesCustomerActiveRewardsApiService";
import { RewardsCustomerRewardsQueryParams } from "../api_services/NudgesCustomerActiveRewardsApiService/query_params/RewardsCustomerRewardsQueryParams";
import {
    RewardsCustomerRewardsResponseSchema,
    RewardsCustomerRewardsResponseType,
} from "../api_services/NudgesCustomerActiveRewardsApiService/response/RewardsCustomerRewardsResponseSchema";
import NudgesRewardsCustomerRewardsStore from "../stores/NudgesRewardsCustomerRewardsStore";
import { nudgesApplicationLoggerToken } from "../tokens";

@singleton()
class NudgesCustomerActiveRewardsService {
    constructor(
        private readonly nudgesCustomerActiveRewardsApiService: NudgesCustomerActiveRewardsApiService,
        private nudgesRewardsCustomerRewardsStore: NudgesRewardsCustomerRewardsStore,
        @inject(nudgesApplicationLoggerToken)
        private readonly logger: LoggerInterface
    ) {}

    getCustomerActiveRewards(
        queryParams: RewardsCustomerRewardsQueryParams
    ): Observable<RewardsCustomerRewardsResponseType> {
        const status = this.nudgesRewardsCustomerRewardsStore.getStatus();
        this.nudgesRewardsCustomerRewardsStore.setStatus(
            status === STATUS.VIRGIN ? STATUS.INITIAL_LOADING : STATUS.LOADING
        );

        return this.nudgesCustomerActiveRewardsApiService
            .getCustomerActiveRewardsRequest(queryParams)
            .pipe(
                map((value) =>
                    create(value.body, RewardsCustomerRewardsResponseSchema)
                ),
                catchError((error: unknown) => {
                    this.logger.debug(
                        "NudgesRewardsCustomerRewardsService.getCustomerActiveRewards is errored",
                        error
                    );
                    this.nudgesRewardsCustomerRewardsStore.setStatus(
                        STATUS.ERROR
                    );
                    throw error;
                }),
                tap((value) => {
                    this.logger.debug(
                        "NudgesRewardsCustomerRewardsService.getCustomerActiveRewards is ready"
                    );
                    //Store Update
                    applyTransaction(() => {
                        this.nudgesRewardsCustomerRewardsStore.update(
                            (state) => ({
                                ...state,
                                customerRewardsList: value.rewards,
                            })
                        );
                        this.nudgesRewardsCustomerRewardsStore.setStatus(
                            STATUS.READY
                        );
                    });
                })
            );
    }
}

export default NudgesCustomerActiveRewardsService;
